/**
 * Shared helpers for rendering uploaded media attachments inline.
 *
 * Videos (mp4/webm/...) get an inline <video> player and images an inline
 * preview, matching the SOP attachments behavior. Reused by task, note, goal,
 * and calendar-event attachment lists.
 */

export type MediaFileLike = {
  name: string;
  fileUrl?: string | null;
  fileType?: string | null;
};

export const VIDEO_TYPES = ["mp4", "webm", "ogg", "quicktime", "mov"];
export const IMAGE_TYPES = ["png", "jpg", "jpeg", "gif", "webp", "svg+xml", "svg", "bmp", "avif"];

function matchesMediaKind(file: MediaFileLike, kinds: string[], dataUrlPrefix: string): boolean {
  if (!file.fileUrl) return false;
  const fileType = (file.fileType || "").toLowerCase();
  if (kinds.includes(fileType)) return true;
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext && kinds.includes(ext)) return true;
  return file.fileUrl.startsWith(`data:${dataUrlPrefix}/`);
}

export function isVideoMediaFile(file: MediaFileLike): boolean {
  return matchesMediaKind(file, VIDEO_TYPES, "video");
}

export function isImageMediaFile(file: MediaFileLike): boolean {
  return !isVideoMediaFile(file) && matchesMediaKind(file, IMAGE_TYPES, "image");
}

/**
 * Classify a media file from its metadata alone (fileType/name), for callers
 * whose list endpoints strip the heavy fileUrl (e.g. notes). Returns null for
 * anything that is not an uploaded video/image.
 */
export function mediaKindFromMetadata(file: { name: string; fileType?: string | null }): "video" | "image" | null {
  const meta = { ...file, fileUrl: "x" }; // satisfy the fileUrl presence check
  if (matchesMediaKind(meta, VIDEO_TYPES, "video")) return "video";
  if (matchesMediaKind(meta, IMAGE_TYPES, "image")) return "image";
  return null;
}

/** Inline player/preview body. Render below the attachment's header row. */
export function InlineMediaPreview({ file, testId }: { file: MediaFileLike; testId: string }) {
  if (!file.fileUrl) return null;
  if (isVideoMediaFile(file)) {
    return (
      <div className="aspect-video w-full bg-black">
        <video
          src={file.fileUrl}
          controls
          preload="metadata"
          className="w-full h-full"
          data-testid={`video-${testId}`}
        />
      </div>
    );
  }
  if (isImageMediaFile(file)) {
    return (
      <div className="w-full bg-muted/20 flex justify-center">
        <img
          src={file.fileUrl}
          alt={file.name}
          className="max-h-96 w-auto max-w-full object-contain"
          data-testid={`image-${testId}`}
        />
      </div>
    );
  }
  return null;
}
